import React, { useState, useEffect } from "react";
import {
  Plus,
  X,
  Trash2,
  FileText,
  List,
  Check,
  ChevronDown,
  ChevronUp,
  Calendar,
  CheckSquare,
  Edit3,
  PenTool,
  GripVertical,
  Layers,
  Save,
} from "lucide-react";
import { toast } from "react-toastify";

const QUESTION_TYPES = {
  SHORT_TEXT: "short_text",
  LONG_TEXT: "long_text",
  SINGLE_CHOICE: "single_choice",
  MULTIPLE_CHOICE: "multiple_choice",
  DROPDOWN: "dropdown",
  DATE: "date",
  SIGNATURE: "signature",
};

/**
 * Motivational Questions Section Component
 * For conferences only - allows adding sections and questions with various types
 */
const MotivationalQuestionsSection = ({
  value = [],
  onChange,
  className = "",
}) => {
  const [sections, setSections] = useState(value || []);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [newSection, setNewSection] = useState({
    sectionTitle: "",
    questions: [],
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    type: QUESTION_TYPES.SHORT_TEXT,
    options: [],
    required: true,
    placeholder: "",
  });
  const [newOption, setNewOption] = useState("");
  const [addingQuestionToSection, setAddingQuestionToSection] = useState(null);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      const normalized = value.map((section) => ({
        ...section,
        questions: Array.isArray(section.questions) ? section.questions : [],
      }));
      setSections(normalized);
      // Default all sections to expanded; only set true for indices not already in state (preserve user collapse/expand)
      if (normalized.length > 0) {
        setExpandedSections((prev) => {
          const next = { ...prev };
          normalized.forEach((_, i) => {
            if (next[i] === undefined) next[i] = true;
          });
          return next;
        });
      }
    }
  }, [value]);

  const handleAddSection = () => {
    const trimmedTitle = newSection.sectionTitle.trim();

    if (!trimmedTitle) {
      toast.error("Please enter a section title");
      return;
    }

    if (editingSectionIndex !== null) {
      // Update existing section
      const updatedSections = [...sections];
      updatedSections[editingSectionIndex] = {
        ...updatedSections[editingSectionIndex],
        sectionTitle: trimmedTitle,
      };
      setSections(updatedSections);
      onChange(updatedSections);
      toast.success("Section updated");
    } else {
      // Add new section
      const sectionToAdd = {
        sectionTitle: trimmedTitle,
        questions: [],
      };
      const updatedSections = [...sections, sectionToAdd];
      setSections(updatedSections);
      onChange(updatedSections);
      setExpandedSections({ ...expandedSections, [updatedSections.length - 1]: true });
      toast.success("Section added");
    }

    setNewSection({ sectionTitle: "", questions: [] });
    setShowSectionForm(false);
    setEditingSectionIndex(null);
  };

  const handleRemoveSection = (sectionIndex) => {
    const updatedSections = sections.filter((_, i) => i !== sectionIndex);
    setSections(updatedSections);
    onChange(updatedSections);
    toast.success("Section removed");
  };

  const handleEditSection = (sectionIndex) => {
    setNewSection({ sectionTitle: sections[sectionIndex].sectionTitle, questions: [] });
    setEditingSectionIndex(sectionIndex);
    setShowSectionForm(true);
  };

  const handleAddQuestion = (sectionIndex) => {
    const trimmedQuestion = newQuestion.question.trim();

    if (!trimmedQuestion) {
      toast.error("Please enter a question");
      return;
    }

    const requiresOptions = [
      QUESTION_TYPES.SINGLE_CHOICE,
      QUESTION_TYPES.MULTIPLE_CHOICE,
      QUESTION_TYPES.DROPDOWN,
    ].includes(newQuestion.type);

    if (requiresOptions && newQuestion.options.length < 2) {
      toast.error("This question type requires at least 2 options");
      return;
    }

    const questionToAdd = {
      question: trimmedQuestion,
      type: newQuestion.type,
      required: newQuestion.required,
      placeholder: newQuestion.placeholder || "",
      ...(requiresOptions && {
        options: newQuestion.options,
      }),
    };

    const updatedSections = [...sections];
    const section = updatedSections[sectionIndex];
    updatedSections[sectionIndex] = {
      ...section,
      questions: [...(section.questions ?? []), questionToAdd],
    };
    setSections(updatedSections);
    onChange(updatedSections);
    setNewQuestion({
      question: "",
      type: QUESTION_TYPES.SHORT_TEXT,
      options: [],
      required: true,
      placeholder: "",
    });
    setAddingQuestionToSection(null);
    toast.success("Question added");
  };

  const handleRemoveQuestion = (sectionIndex, questionIndex) => {
    const updatedSections = [...sections];
    const section = updatedSections[sectionIndex];
    updatedSections[sectionIndex] = {
      ...section,
      questions: (section.questions ?? []).filter((_, i) => i !== questionIndex),
    };
    setSections(updatedSections);
    onChange(updatedSections);
    toast.success("Question removed");
  };

  const handleAddOption = () => {
    const trimmedOption = newOption.trim();

    if (!trimmedOption) {
      toast.error("Please enter an option");
      return;
    }

    if (newQuestion.options.includes(trimmedOption)) {
      toast.warning("This option already exists");
      return;
    }

    const updatedOptions = [...newQuestion.options, trimmedOption];
    setNewQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
    setNewOption("");
  };

  const handleRemoveOption = (optionIndex) => {
    const updatedOptions = newQuestion.options.filter(
      (_, i) => i !== optionIndex
    );
    setNewQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const handleQuestionTypeChange = (type) => {
    setNewQuestion({
      ...newQuestion,
      type,
      options: [],
    });
  };

  const toggleSection = (sectionIndex) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case QUESTION_TYPES.SHORT_TEXT:
        return <Edit3 className="w-4 h-4" />;
      case QUESTION_TYPES.LONG_TEXT:
        return <FileText className="w-4 h-4" />;
      case QUESTION_TYPES.SINGLE_CHOICE:
        return <List className="w-4 h-4" />;
      case QUESTION_TYPES.MULTIPLE_CHOICE:
        return <CheckSquare className="w-4 h-4" />;
      case QUESTION_TYPES.DROPDOWN:
        return <ChevronDown className="w-4 h-4" />;
      case QUESTION_TYPES.DATE:
        return <Calendar className="w-4 h-4" />;
      case QUESTION_TYPES.SIGNATURE:
        return <PenTool className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case QUESTION_TYPES.SHORT_TEXT:
        return "Short Text";
      case QUESTION_TYPES.LONG_TEXT:
        return "Long Text";
      case QUESTION_TYPES.SINGLE_CHOICE:
        return "Single Choice";
      case QUESTION_TYPES.MULTIPLE_CHOICE:
        return "Multiple Choice";
      case QUESTION_TYPES.DROPDOWN:
        return "Dropdown";
      case QUESTION_TYPES.DATE:
        return "Date";
      case QUESTION_TYPES.SIGNATURE:
        return "Signature";
      default:
        return "Unknown";
    }
  };

  const getTotalQuestions = () => {
    return sections.reduce((total, section) => total + (section.questions?.length ?? 0), 0);
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Layers className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">
              Registration Form Builder
            </h3>
            <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
              {sections.length} {sections.length === 1 ? "Section" : "Sections"}
            </span>
            <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
              {getTotalQuestions()} {getTotalQuestions() === 1 ? "Question" : "Questions"}
            </span>
          </div>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Build a comprehensive registration form with sections and various question types.
          Add sections in order; add questions to each section, then add the next section below.
        </p>

        {/* Sections List */}
        {sections.length > 0 ? (
          <div className="space-y-4">
            {sections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="bg-gradient-to-br from-purple-50 to-white rounded-lg border-2 border-purple-200 overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex items-center justify-between p-4 bg-purple-100/50 border-b border-purple-200">
                  <div className="flex items-center space-x-3">
                    <GripVertical className="w-4 h-4 text-purple-400" />
                    <h4 className="text-sm font-bold text-gray-900 uppercase">
                      {section.sectionTitle}
                    </h4>
                    <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-200 rounded-full">
                      {(section.questions?.length ?? 0)} {(section.questions?.length ?? 0) === 1 ? "Question" : "Questions"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditSection(sectionIndex)}
                      className="p-1.5 text-purple-600 rounded hover:bg-purple-200 transition-colors"
                      title="Edit section"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSection(sectionIndex)}
                      className="p-1.5 text-red-600 rounded hover:bg-red-100 transition-colors"
                      title="Remove section"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleSection(sectionIndex)}
                      className="p-1.5 text-purple-600 rounded hover:bg-purple-200 transition-colors"
                    >
                      {expandedSections[sectionIndex] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Section Content */}
                {expandedSections[sectionIndex] && (
                  <div className="p-4 space-y-4">
                    {/* Questions List */}
                    {(section.questions?.length ?? 0) > 0 && (
                      <div className="space-y-3">
                        {(section.questions ?? []).map((question, questionIndex) => (
                          <div
                            key={questionIndex}
                            className="p-3 bg-white rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-purple-600 text-white text-xs font-bold rounded-full">
                                    {questionIndex + 1}
                                  </span>
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    {question.question}
                                    {question.required && (
                                      <span className="text-red-500 ml-1">*</span>
                                    )}
                                  </h5>
                                </div>
                                <div className="flex items-center space-x-2 ml-8">
                                  <span className="flex items-center space-x-1 px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
                                    {getQuestionTypeIcon(question.type)}
                                    <span>{getQuestionTypeLabel(question.type)}</span>
                                  </span>
                                </div>
                                {/* Show options for choice-based questions */}
                                {[
                                  QUESTION_TYPES.SINGLE_CHOICE,
                                  QUESTION_TYPES.MULTIPLE_CHOICE,
                                  QUESTION_TYPES.DROPDOWN,
                                ].includes(question.type) &&
                                  question.options &&
                                  question.options.length > 0 && (
                                    <div className="mt-2 ml-8 space-y-1">
                                      {question.options.map((option, optIndex) => (
                                        <div
                                          key={optIndex}
                                          className="flex items-center space-x-2 text-xs text-gray-600"
                                        >
                                          <Check className="w-3 h-3 text-purple-500" />
                                          <span>{option}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveQuestion(sectionIndex, questionIndex)
                                }
                                className="flex-shrink-0 p-1.5 ml-3 text-red-600 rounded hover:bg-red-50"
                                title="Remove question"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Question to Section */}
                    {addingQuestionToSection === sectionIndex ? (
                      <div className="p-4 bg-white rounded-lg border-2 border-purple-300">
                        <h5 className="mb-3 text-sm font-bold text-gray-900">
                          Add Question to {section.sectionTitle}
                        </h5>
                        <div className="space-y-4">
                          {/* Question Text */}
                          <div>
                            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                              Question <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={newQuestion.question}
                              onChange={(e) =>
                                setNewQuestion((prev) => ({
                                  ...prev,
                                  question: e.target.value,
                                }))
                              }
                              placeholder="e.g., What is your full name?"
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white resize-y"
                            />
                          </div>

                          {/* Question Type Selector */}
                          <div>
                            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                              Question Type <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {Object.entries(QUESTION_TYPES).map(([key, value]) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => handleQuestionTypeChange(value)}
                                  className={`flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium border-2 rounded-lg transition-all ${
                                    newQuestion.type === value
                                      ? "border-purple-500 bg-purple-50 text-purple-700"
                                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                                  }`}
                                >
                                  {getQuestionTypeIcon(value)}
                                  <span>{getQuestionTypeLabel(value)}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Placeholder (for text inputs) */}
                          {[QUESTION_TYPES.SHORT_TEXT, QUESTION_TYPES.LONG_TEXT].includes(
                            newQuestion.type
                          ) && (
                            <div>
                              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                                Placeholder Text (Optional)
                              </label>
                              <input
                                type="text"
                                value={newQuestion.placeholder}
                                onChange={(e) =>
                                  setNewQuestion((prev) => ({
                                    ...prev,
                                    placeholder: e.target.value,
                                  }))
                                }
                                placeholder="e.g., Enter your answer here..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          )}

                          {/* Options (for choice-based questions) */}
                          {[
                            QUESTION_TYPES.SINGLE_CHOICE,
                            QUESTION_TYPES.MULTIPLE_CHOICE,
                            QUESTION_TYPES.DROPDOWN,
                          ].includes(newQuestion.type) && (
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <label className="block mb-2 text-xs font-semibold text-gray-700">
                                Options <span className="text-red-500">*</span>
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  (At least 2 required)
                                </span>
                              </label>

                              {/* Existing Options */}
                              {newQuestion.options.length > 0 && (
                                <div className="mb-3 space-y-2">
                                  {newQuestion.options.map((option, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-purple-600 text-white text-xs font-bold rounded-full">
                                          {index + 1}
                                        </span>
                                        <span className="text-sm text-gray-900">{option}</span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveOption(index)}
                                        className="p-1 text-red-600 rounded hover:bg-red-50"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Add Option Input */}
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={newOption}
                                  onChange={(e) => setNewOption(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddOption();
                                    }
                                  }}
                                  placeholder="Enter an option..."
                                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <button
                                  type="button"
                                  onClick={handleAddOption}
                                  disabled={!newOption.trim()}
                                  className="px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Required Toggle */}
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`required-${sectionIndex}`}
                              checked={newQuestion.required}
                              onChange={(e) =>
                                setNewQuestion((prev) => ({
                                  ...prev,
                                  required: e.target.checked,
                                }))
                              }
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <label
                              htmlFor={`required-${sectionIndex}`}
                              className="text-xs font-medium text-gray-700"
                            >
                              This question is required
                            </label>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center gap-3 pt-2">
                            <button
                              type="button"
                              onClick={() => handleAddQuestion(sectionIndex)}
                              disabled={
                                !newQuestion.question.trim() ||
                                ([
                                  QUESTION_TYPES.SINGLE_CHOICE,
                                  QUESTION_TYPES.MULTIPLE_CHOICE,
                                  QUESTION_TYPES.DROPDOWN,
                                ].includes(newQuestion.type) &&
                                  newQuestion.options.length < 2)
                              }
                              className="flex items-center justify-center gap-2 px-6 py-3 text-base font-bold text-white bg-green-600 rounded-xl shadow-md hover:bg-green-700 focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <Save className="w-5 h-5" strokeWidth={2.5} />
                              <span>Save</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setAddingQuestionToSection(null);
                                setNewQuestion({
                                  question: "",
                                  type: QUESTION_TYPES.SHORT_TEXT,
                                  options: [],
                                  required: true,
                                  placeholder: "",
                                });
                              }}
                              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingQuestionToSection(sectionIndex)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold text-purple-700 bg-white rounded-lg border-2 border-dashed border-purple-300 hover:bg-purple-50 transition-colors w-full justify-center"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Question</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Layers className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              No sections added yet
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Add your first section below to start building your registration form
            </p>
          </div>
        )}

        {/* Add Section - always at bottom for sequential flow (Section 1 → questions → Section 2 → …) */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          {!showSectionForm ? (
            <button
              type="button"
              onClick={() => {
                setShowSectionForm(true);
                setEditingSectionIndex(null);
                setNewSection({ sectionTitle: "", questions: [] });
              }}
              className="flex items-center justify-center space-x-2 w-full py-3 px-4 text-sm font-semibold text-purple-700 bg-purple-50 rounded-lg border-2 border-dashed border-purple-200 hover:bg-purple-100 hover:border-purple-300 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>
                {sections.length === 0
                  ? "Add first section"
                  : "Add next section"}
              </span>
            </button>
          ) : (
            <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
              <h4 className="mb-3 text-sm font-bold text-gray-900">
                {editingSectionIndex !== null ? "Edit Section" : "Add New Section"}
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    Section Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newSection.sectionTitle}
                    onChange={(e) =>
                      setNewSection((prev) => ({
                        ...prev,
                        sectionTitle: e.target.value,
                      }))
                    }
                    placeholder="e.g., SECTION 1: PERSONAL INFORMATION"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleAddSection}
                    disabled={!newSection.sectionTitle.trim()}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    <span>{editingSectionIndex !== null ? "Update" : "Add"} Section</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSectionForm(false);
                      setEditingSectionIndex(null);
                      setNewSection({ sectionTitle: "", questions: [] });
                    }}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MotivationalQuestionsSection;

